import React, { useEffect, useState } from "react";

import { cloneDeep, includes, keys, isEmpty, set, get, omit, isEqual } from "lodash";
import moment from "moment";
import { Button, ButtonToolbar, Message, useToaster, Loader } from "rsuite";

import PatientForm from "./partial/_form";
import RespForm from "./partial/_rrsetting";
import VisitForm from "./partial/_visitsetting";
import BedExitForm from "./partial/_bedexitsetting";
import useRequestHandler from "hooks/useRequestHandler";
import Api from "util/Api";
import { uniqueValuesArray } from "./partial/util";

import {
    requiredVisitTimerData,
    visitSoonTimerOptionsData,
    bedExitRangeData,
    rrUrgentTimerOptions,
    bedExitData,
    lowWarnOptions,
    rrWarningTimerOptions
} from "./partial/util";

const ResidentContent = ({ auth, match, history }) => {

    const demo = auth.isDemoUser();

    const averagePatientAge = 65;
    const startingDate = moment().subtract(averagePatientAge, "years");

    const defaultBedExit = {
        active: false,
        allday: false,
        rangeend_timeofday: "08:00",
        rangestart_timeofday: "22:00",
        span: 120,
        timezone: "America/New_York",
    };

    const defaultRr = {
        normal: 16,
        low: 15,
        high_urgent_alert_time: 60,
        high_warn_alert_time: 30,
        low_urgent_alert_time: 60,
        low_warn_alert_time: 30,
    };

    const defaultVisit = {
        active: true,
        require_visit: 60,
        visit_soon: 20,
    };

    const defaultSetting = {
        bedexit: defaultBedExit,
        rr: defaultRr,
        visit: defaultVisit,
    };

    const [isLoading, setIsLoading] = useState(false);
    const [freeRooms, setFreeRooms] = useState([]);
    const [disabledField, setDisabledField] = useState({
        section: true,
        room: true,
        bed: true,
    });
    const [availableLocations, setAvailableLocations] = useState([]);
    const [availableSections, setAvailableSections] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]);
    const [availableBeds, setAvailableBeds] = useState([]);
    const [facility, setFacility] = useState(null);
    const [section, setSection] = useState("");
    const [room, setRoom] = useState("");
    const [bed, setBed] = useState("");
    const [originalPatient, setOriginalPatient] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        dob: startingDate,
    });
    const [patient, setPatient] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        dob: startingDate,
        atRisk: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveChangesButtonText, setSaveChangesButtonText] = useState("Save");
    const [requiredFieldClasses, setRequiredFieldClasses] = useState({
        firstName: "required",
        lastName: "required",
        gender: "required",
        dob: "required",
    });

    const [current, setCurrent] = useState({ setting: null });
    const [pending, setPending] = useState({ setting: null });

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalQuestion, setModalQuestion] = useState("");
    const [modalText, setModalText] = useState("Are you sure?");
    const toastMessage = useToaster();

    useEffect(async () => {
        setIsLoading(true);
        await getAllData();
        setIsLoading(false);
    }, []);

    const getPatientRequest = useRequestHandler({
        auth,
        apiArguments: [{ id: match.params.id }],
        apiName: "getPatient",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Warning",
            message: "Unable to load patient data, just reload page.",
            type: "info"
        }
    });

    const getPatient = async (location) => {
        await getPatientRequest(data => {

            if (data.section) {
                const availableSections = location
                    .find(item => item.name === data.facility)?.sections?.sectionNames
                    .map(item => ({ label: item, value: item }));

                setFacility(data.facility);
                setSection(data.section);
                setRoom(data.room_name);
                setBed(data.bed);
                setAvailableSections(availableSections);
                setDisabledField({ section: false });
                getBedsNoIndex(data.facility, data.section, data);
            }
            setPatient(data);
            setOriginalPatient(cloneDeep(data));
        });
    }

    const setSettings = (setting) => {
        setCurrent({ ...current.setting, setting });
        setPending({ ...pending.setting, setting });
    }

    const getPatientSettings = async () => {

        await Api.withAuth(auth).getSetting({ id: match.params.id })
            .then((data) => {
                setSettings(data);
            })
            .catch(() => {
                setSettings(defaultSetting);
            })
    }

    const getPatientDefaultSettings = async () => {
        await Api.withAuth(auth).getDefaultSetting({ orgId: auth.currentUser.defaultOrg })
            .then((data) => {
                setSettings(data);
            })
            .catch(() => {
                setSettings(defaultSetting);
            })
    }

    const getLocationDataRequest = useRequestHandler({
        auth,
        apiArguments: [auth.currentUser.defaultOrg],
        apiName: "getFacilities",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Warning",
            message: "Unable to load facilities data, please try again later.",
            type: "info"
        }
    })

    const getAllData = async () => {
        await getLocationDataRequest(data => {
            setAvailableLocations(data.map(item => ({ name: item.name, sections: item.sections.sectionNames })));
            if (match.params.id) {
                getPatient(data);
                getPatientSettings();
            } else {
                getPatientDefaultSettings();
            }
        })
    }

    const getBedsNoIndex = async (facility, section, patient) => {

        try {
            await Api.withAuth(auth).getBedsNoIndex({ facility, section })
                .then(data => {
                    const freemRoom = data.filter(({ id, occupied }) => id === patient?.bed || !occupied);
                    const availableRooms = uniqueValuesArray(freemRoom, "room_name");
                    setFreeRooms(freemRoom);
                    setAvailableRooms(availableRooms);
                    setDisabledField({ room: false, bed: true });
                    setAvailableBeds([]);

                    if (patient?.room_name) {
                        const bedsData = freemRoom.filter(({ room_name }) => room_name === patient.room_name);
                        const availableBeds = uniqueValuesArray(bedsData, "bed_name");
                        setAvailableBeds(availableBeds);
                        setDisabledField({ room: false });
                    };
                })

        } catch (error) {
            toastMessage.push(
                <Message type="info" duration={2000}>
                    <h6>Warning</h6>
                    <p className="m-0">Unable to load beds, just reload page or will try later</p>
                </Message>,
                {
                    placement: "topEnd"
                });
        }
    }

    const handleCancelChanges = () => history.push(`/configuration/residents`);

    const changeLocationHandler = (value) => {

        if (value) {
            const availableSections = availableLocations
                .find(item => item.name === value)?.sections
                .map(item => ({ label: item, value: item }));

            setAvailableSections(availableSections);
            setFacility(value);
            setDisabledField({
                section: false,
                room: true,
                bed: true,
            });
            setAvailableRooms([]);
            setAvailableBeds([]);
        } else {
            setDisabledField({
                section: true,
                room: true,
                bed: true,
            });
            setFacility(null);
            setPatient({ ...patient, bed: null });
            setAvailableSections([]);
            setAvailableRooms([]);
            setAvailableBeds([]);
        }
    }

    const changeSectionHandler = (value) => {
 
        if (value) {
            setSection(value);
            getBedsNoIndex(facility, value);
            setDisabledField({
                section: false,
                room: false,
                bed: true,
            });
        } else {
            setDisabledField({
                section: false,
                room: true,
                bed: true,
            });
            setSection(null);
            setPatient({ ...patient, bed: null });
            setAvailableRooms([]);
            setAvailableBeds([]);
        }
    }

    const changeRoomHandler = (value) => {
     
        if (value) {
            const bedsData = freeRooms.filter(({ room_name }) => room_name === value);

            if(bedsData.length === 1 && !bedsData[0]?.bed_name) {
                const bedId = bedsData[0].id;
                setBed(bedId);
                setPatient({ ...patient, "bed": bedId });
            }
            const availableBeds = uniqueValuesArray(bedsData, "bed_name");
            setRoom(value);
            setAvailableBeds(availableBeds);
            setDisabledField({ bed: false });
        } else {
            setAvailableBeds([]);
            setRoom(null);
            setPatient({ ...patient, bed: null });
            setDisabledField({
                section: false,
                room: false,
                bed: true,
            });
        }
    }

    const handleInputPickerChange = async (name, value) => {

        if (name === "location") changeLocationHandler(value);
        if (name === "section") changeSectionHandler(value);
        if (name === "room") changeRoomHandler(value);

        if (value && name === "bed") {
            setBed(value);
            setPatient({ ...patient, [name]: value });
        }
    }

    const handleChangePatient = (name, value) => {
        setPatient({ ...patient, [name]: value });
    }

    const validateRequired = (name, value) => {
        if (includes(keys(requiredFieldClasses), name)) {

            setRequiredFieldClasses({
                ...requiredFieldClasses,
                [name]: `required ${isEmpty(value) ? "invalid" : ""}`
            });
        }
    }

    const handleChangeRsuite = (name, value) => {
        const newCurrent = cloneDeep(current);

        set(newCurrent, name, value);
        setCurrent({ ...newCurrent });
    }

    const handleResetDefaultVisits = () => {
        setIsModalVisible(true);
        setModalQuestion("RESET_VISITS");
        setModalText("Are you sure to reset Visit Tracker?");
    }

    const handleResetDefaultRespiration = () => {
        setIsModalVisible(true);
        setModalQuestion("RESET_RESPIRATION");
        setModalText("Are you sure to reset Respiration Range?");
    }

    const handleResetDefaultBedExit = () => {
        setIsModalVisible(true);
        setModalQuestion("RESET_BEDEXIT");
        setModalText("Are you sure to reset Bed Exit?");
    }

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setModalQuestion(null);
    }

    const handleModalConfirm = () => {

        switch (modalQuestion) {
            case "RESET_VISITS":
                set(current, "setting.visit", pending.setting.visit);
                break;
            case "RESET_RESPIRATION":
                set(current, "setting.rr", pending.setting.rr);
                break;
            case "RESET_BEDEXIT":
                set(current, "setting.bedexit", pending.setting.bedexit);
                break;
            default:
                break;
        }

        setCurrent({ ...current });
        setIsModalVisible(false);
        setModalQuestion(null);
    }

    const hasPendingChanges = () => {
        return !isEqual(patient, originalPatient) || !isEqual(current, pending);
    }

    const createPatientConfTabRequest = useRequestHandler({
        auth,
        apiArguments: [{ orgId: auth.currentUser.defaultOrg, patient: patient }],
        apiName: "createPatientConfTab",
        successMessageData: {
            message: "Resident are successfully added.",
            duration: 3000
        },
        errorMessageData: {
            title: "Warning",
            message: "Unable to create new resident, just reload page or will try later.",
            type: "info"
        }
    });

    const updatedPatientData = () => {
        return omit(patient, ["device", "facility", "room_name", "section", "bed_name"]);
    }

    const editPatientRequest = useRequestHandler({
        auth,
        apiArguments: [{ id: match.params.id, patient: updatedPatientData() }],
        apiName: "editPatientConfTab",
        successMessageData: {
            message: "Resident are successfully updated.",
            duration: 3000
        },
        errorMessageData: {
            title: "Warning",
            message: "Unable to update the resident, just reload page or will try later.",
            type: "info"
        }
    });

    const updateSettingRequest = useRequestHandler({
        auth,
        apiArguments: [{ id: match.params.id, setting: current.setting }],
        apiName: "setSetting",
        successMessageData: { show: false },
        errorMessageData: {
            title: "Warning",
            message: "Unable to update the resident, just reload page or will try later.",
            type: "info"
        }
    });

    const updatePatient = async () => {
        await updateSettingRequest(data => {

            editPatientRequest(patient => {
                setIsSubmitting(false);
                setSaveChangesButtonText("Saved!");
                history.push(`/configuration/residents`);
            })
        })
    }

    const createPatient = async () => {
        await createPatientConfTabRequest(patient => {
            Api.withAuth(auth).setSetting({ id: patient.patid, setting: current.setting })
                .then(data => {
                    setIsSubmitting(false);
                    setSaveChangesButtonText("Saved!");
                    history.push(`/configuration/residents`);
                }
                )
                .catch(() => {
                    toastMessage.push(
                        <Message type="info" duration={2000}>
                            <h6>Warning</h6>
                            <p className="m-0">Unable to save settings data, just reload page and try again</p>
                        </Message>,
                        {
                            placement: "topEnd"
                        });
                })
        })
    }

    const handleSaveChanges = async () => {
        setIsSubmitting(true);
        setSaveChangesButtonText("Saving...");
        if (match.params.id) {
            updatePatient();
        } else {
            createPatient();
        }
    }

    const readyToSubmit = !isSubmitting && hasPendingChanges() &&
        !_(requiredFieldClasses)
            .keys()
            .map((field) => get(patient, field))
            .some(isEmpty);

    const requiredMinutes = current.setting?.visit?.require_visit;
    const soonMinutes = current.setting?.visit?.visit_soon;

    const bedExitFrom = current.setting?.bedexit?.rangestart_timeofday;
    const bedExitTo = current.setting?.bedexit?.rangeend_timeofday;
    const bedExit24Hours = current.setting?.bedexit?.allday;
    const bedExitMinutes = current.setting?.bedexit?.span;
    const bedExitDisabled = current.setting?.bedexit?.active;

    const lowErrorvery_low_warn_alert_time = current.setting?.rr?.low_warn_alert_time;
    const lowErrorvery_low_urgent_alert_time = current.setting?.rr?.low_urgent_alert_time;
    const highErrorvery_high_urgent_alert_time = current.setting?.rr?.high_urgent_alert_time;
    const highErrorvery_high_warn_alert_time = current.setting?.rr?.high_warn_alert_time;
    const normal = current.setting?.rr?.normal;
    const lowVery = current.setting?.rr?.low;
    const lowWarn = 0;

    const handleClearLocation = () => {
        setFacility("");
        setSection("");
        setRoom("");
        setBed("");
        setPatient({ ...patient, bed: null });
    };

    return (
        <>
            {isLoading &&
                <div className="loader-wrapper">
                    <Loader size="lg" center content="Loading data..." />
                </div>}

            <div className={isLoading ? "hidden" : "patient-new"}>

                <div className="row">
                    <div className="col-md-12">
                        <div className="card patient-demographics">
                            <div className="card-header">
                                Demographics
                            </div>

                            <div className="card-body">
                                <PatientForm
                                    availableLocations={availableLocations}
                                    disabledField={disabledField}
                                    availableSections={availableSections}
                                    availableRooms={availableRooms}
                                    availableBeds={availableBeds}
                                    patient={patient}
                                    facility={facility}
                                    section={section}
                                    room={room}
                                    bed={bed}
                                    requiredFieldClasses={requiredFieldClasses}
                                    onChange={handleChangePatient}
                                    onChangeInputPicker={handleInputPickerChange}
                                    onClearLocation={handleClearLocation}
                                    onValidateRequired={validateRequired}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="room-settings">
                    <div className="row">

                        <VisitForm
                            requiredVisitTimerData={requiredVisitTimerData}
                            requiredMinutes={requiredMinutes}
                            visitSoonTimerOptionsData={visitSoonTimerOptionsData}
                            soonMinutes={soonMinutes}
                            handleChange={handleChangeRsuite}
                            handleResetDefaultVisits={handleResetDefaultVisits}
                        />

                        <BedExitForm
                            bedExit24Hours={bedExit24Hours}
                            bedExitRangeData={bedExitRangeData}
                            bedExitFrom={bedExitFrom}
                            bedExitTo={bedExitTo}
                            handleChange={handleChangeRsuite}
                            bedExitDisabled={bedExitDisabled}
                            bedExitData={bedExitData}
                            bedExitMinutes={bedExitMinutes}
                            handleResetDefaultBedExit={handleResetDefaultBedExit}
                        />

                    </div>
                    <RespForm
                        lowWarnOptions={lowWarnOptions}
                        lowErrorvery_low_warn_alert_time={lowErrorvery_low_warn_alert_time}
                        lowErrorvery_low_urgent_alert_time={lowErrorvery_low_urgent_alert_time}
                        handleResetDefaultRespiration={handleResetDefaultRespiration}
                        highErrorvery_high_urgent_alert_time={highErrorvery_high_urgent_alert_time}
                        highErrorvery_high_warn_alert_time={highErrorvery_high_warn_alert_time}
                        rrWarningTimerOptions={rrWarningTimerOptions}
                        rrUrgentTimerOptions={rrUrgentTimerOptions}
                        normal={normal}
                        handleChange={handleChangeRsuite}
                        lowVery={lowVery}
                        lowWarn={lowWarn}
                    />
                    <div className="actions row">
                        <div className="col-md-12 text-right">
                            <ButtonToolbar>
                                <Button className="btn btn-secondary mx-2 px-5" onClick={handleCancelChanges}>Cancel</Button>
                                <Button className="btn btn-primary mx-2 px-5" onClick={handleSaveChanges} disabled={!readyToSubmit || !hasPendingChanges || demo}>
                                    {saveChangesButtonText}
                                    {demo && " [DEMO]"}
                                </Button>
                            </ButtonToolbar>
                        </div>
                    </div>

                    {isModalVisible &&
                        <div>
                            <div className="popup-wrapper" onClick={handleModalCancel} role="dialog">
                                <div className="popup" onClick={(e) => e.stopPropagation()}>
                                    <span className="close" onClick={handleModalCancel}>Close</span>

                                    <h1>Confirm</h1>
                                    <p>{modalText}</p>

                                    <div className="actions text-center">
                                        <ButtonToolbar>
                                            <Button className="btn btn-secondary mx-2 px-5" onClick={handleModalCancel}>No</Button>
                                            <Button className="btn btn-primary mx-2 px-5" onClick={handleModalConfirm}>Yes</Button>
                                        </ButtonToolbar>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </>);

}

export default ResidentContent;